// src/pages/CreateCirclePage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Preferences } from "@capacitor/preferences";
import { api } from "api";
import AppHeader from "../components/AppHeader";

/* ---------- tiny flag helper ---------- */
function flagEmoji(countryCode) {
  if (!countryCode || countryCode.length !== 2) return "🏳️";
  const cc = countryCode.toUpperCase();
  const codePoints = [cc.charCodeAt(0), cc.charCodeAt(1)].map((c) => 127397 + c);
  return String.fromCodePoint(...codePoints);
}

function getCountryName(code) {
  try {
    if (!code) return "";
    const dn = new Intl.DisplayNames(["en"], { type: "region" });
    return dn.of(code) || "";
  } catch {
    // minimal fallback names for common codes
    const map = { US: "United States", CA: "Canada", GB: "United Kingdom", KR: "South Korea", JP: "Japan", DE: "Germany", FR: "France", IT: "Italy", ES: "Spain", AU: "Australia", SG: "Singapore" };
    return map[code] || code;
  }
}

/* ---------- iOS-safe auth ---------- */
async function getToken() {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}
  if (typeof window !== "undefined") {
    return (
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null
    );
  }
  return null;
}
async function getAuthHeaders() {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function CreateCirclePage() {
  const navigate = useNavigate();

  // for header
  const [user, setUser] = useState(null);
  // form fields
  const [name, setName] = useState("");
  const [city, setCity] = useState("");           // free-text city
  const [countryCode, setCountryCode] = useState(""); // ISO-2, e.g., "US"
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Build country list from Intl (no hardcoded list, no API)
  const countryOptions = useMemo(() => {
    try {
      const regions =
        (typeof Intl?.supportedValuesOf === "function" &&
          Intl.supportedValuesOf("region")) ||
        ["US", "CA", "GB", "KR", "JP", "DE", "FR", "IT", "ES", "AU", "SG"]; // small fallback
      return regions
        .map((code) => {
          const name = getCountryName(code);
          return name ? { code, name } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      // ultimate fallback
      return [
        { code: "US", name: "United States" },
        { code: "CA", name: "Canada" },
        { code: "GB", name: "United Kingdom" },
        { code: "KR", name: "South Korea" },
        { code: "JP", name: "Japan" }
      ];
    }
  }, []);

  // derived label like "New York, United States"
  const locationLabel = useMemo(() => {
    const countryName = countryCode ? getCountryName(countryCode) : "";
    if (city && countryName) return `${city}, ${countryName}`;
    if (countryName) return countryName;
    return city || "";
  }, [city, countryCode]);

  // bootstrap user for header
  useEffect(() => {
    (async () => {
      try {
        const headers = await getAuthHeaders();
        if (!headers.Authorization) return;
        const me = await api.get("/users/me", { headers });
        setUser(me.data || null);
      } catch {
        setUser(null);
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const okLabel = (locationLabel || "").trim();
    if (!name.trim()) return;

    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const normalizedWebsite = website.trim()
        ? (/^https?:\/\//i.test(website.trim()) ? website.trim() : `https://${website.trim()}`)
        : undefined;

      const payload = {
        name: name.trim(),
        // legacy + normalized fields (no Maps required)
        location: okLabel,
        location_label: okLabel,
        country_code: countryCode || undefined,
        // lat/lng omitted since we’re not using Maps here
        website: normalizedWebsite,
        description: description.trim() || undefined
      };

      const res = await api.post("/groups", payload, { headers });
      const id = res?.data?.id;
      if (id) navigate(`/circles/${id}`);
      else alert("Circle created, but no id returned.");
    } catch (err) {
      console.error("Create circle failed:", err);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to create circle.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // same container sizing as Home
  const container = "max-w-7xl mx-auto px-6 md:px-10 xl:px-16";

  /* ------------------------- RENDER (no JSX) ------------------------- */
  return React.createElement(
    "div",
    { className: "app-shell bg-blue-50" },

    // Header
    React.createElement(
      "header",
      { className: "app-header" },
      React.createElement(AppHeader, { user })
    ),

    // Main shell (matches Home)
    React.createElement(
      "main",
      {
        className: `app-content w-full ${container} pt-[calc(64px+var(--safe-top))] md:mt-4 px-0 py-8`
      },

      // 3-column grid like Home: [left | center | right]
      React.createElement(
        "div",
        {
          className:
            "grid grid-cols-1 md:grid-cols-[1fr_minmax(0,2fr)_1fr] gap-10 md:overflow-visible",
          style: { height: "auto" }
        },

        // left column (empty spacer to mirror Home)
        React.createElement("div", { className: "hidden md:block" }),

        // center column: the card with the form
        React.createElement(
          "div",
          { className: "min-w-0" },
          React.createElement(
            "div",
            { className: "bg-transparent rounded-2xl pb-2 rounded-2xl md:p-8 space-y-3" },

            // Title + caption
            React.createElement(
              "div",
              { className: "space-y-0" },
              React.createElement(
                "h1",
                { className: "text-lg ml-3 font-semibold text-blue-500" },
                "Create a New Circle"
              ),
              React.createElement(
                "p",
                { className: "text-sm ml-3 text-gray-500" },
                "Fields marked with ",
                React.createElement("span", { className: "text-red-500" }, "*"),
                " are required"
              )
            ),

            // Form
            React.createElement(
              "form",
              { onSubmit: onSubmit, className: "space-y-2" },

              // Circle Name (required)
              React.createElement(
                "div",
                null,
                React.createElement(
                  "label",
                  { className: "block ml-3 text-sm font-medium mb-1" },
                  "Circle Name ",
                  React.createElement("span", { className: "text-red-500" }, "*")
                ),
                React.createElement("input", {
                  type: "text",
                  value: name,
                  onChange: (e) => setName(e.target.value),
                  required: true,
                  className:
                    "w-full rounded-2xl border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                })
              ),

              // City + Country (side by side)
              React.createElement(
                "div",
                { className: "flex flex-col text-sm md:flex-row md:items-start gap-4" },

                // City
                React.createElement(
                  "div",
                  { className: "flex-1" },
                  React.createElement(
                    "label",
                    { className: "block text-sm ml-3 font-medium mb-1" },
                    "City "
                  ),
                  React.createElement("input", {
                    type: "text",
                    placeholder: "e.g. New York",
                    value: city,
                    onChange: (e) => setCity(e.target.value),
                    className:
                      "w-full rounded-2xl border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  })
                ),

                // Country
                React.createElement(
                  "div",
                  { className: "flex-1" },
                  React.createElement(
                    "label",
                    { className: "block text-sm ml-3 font-medium mb-1" },
                    "Country ",
                  ),
                  React.createElement(
                    "div",
                    { className: "relative" },
                    React.createElement(
                      "select",
                      {
                        value: countryCode,
                        onChange: (e) => setCountryCode(e.target.value),
                        className:
                          "w-full rounded-2xl border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                      },
                      React.createElement(
                        "option",
                        { value: "" },
                        "Select country"
                      ),
                      ...countryOptions.map((opt) =>
                        React.createElement(
                          "option",
                          { key: opt.code, value: opt.code },
                          opt.name
                        )
                      )
                    ),
                    React.createElement("div", {
                      className:
                        "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    })
                  )
                )
              ),

              // Location preview (flag + "City, Country")
              React.createElement(
                "div",
                { className: "ml-3 text-sm text-gray-600" },
                countryCode ? `${flagEmoji(countryCode)} ${locationLabel}` : locationLabel
              ),

              // Website (optional)
              React.createElement(
                "div",
                null,
                React.createElement(
                  "label",
                  { className: "block ml-3 text-sm font-medium mb-1" },
                  "Website"
                ),
                React.createElement("input", {
                  type: "text",
                  inputMode: "url",
                  autoCapitalize: "off",
                  placeholder: "solidcore.co",
                  value: website,
                  onChange: (e) => setWebsite(e.target.value),
                  className:
                    "w-full rounded-2xl border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                })
              ),

              // Description (optional)
              React.createElement(
                "div",
                null,
                React.createElement(
                  "label",
                  { className: "block ml-3 text-sm font-medium mb-1" },
                  "Description"
                ),
                React.createElement("textarea", {
                  placeholder: "Tell us about your circle!",
                  value: description,
                  onChange: (e) => setDescription(e.target.value),
                  rows: 4,
                  className:
                    "w-full rounded-2xl border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                })
              ),

              // Submit
              React.createElement(
                "button",
                {
                  type: "submit",
                  disabled: submitting || !name.trim(),
                  className:
                    "w-full bg-blue-600 text-white rounded-3xl !min-h-0 !min-w-0 px-3 py-1.5 disabled:opacity-50 hover:bg-blue-700"
                },
                submitting ? "Creating…" : "Create Circle"
              )
            )
          )
        ),

        // right column (empty spacer to mirror Home)
        React.createElement("div", { className: "hidden md:block" })
      )
    )
  );
}

export default CreateCirclePage;
